import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import type { FilterState } from '@aviata/offers-filters-fns';

import {
  searchOffersLib,
  searchOffersTypes,
} from '@/features/search-offers-filter';

export const formatDurationLabel = (seconds: number) => {
  if (!seconds) {
    return '';
  }
  const { t } = useI18n();
  const dur = dayjs.duration(seconds, 'seconds');

  const days = dur.days();
  const hours = dur.hours();
  const minutes = dur.minutes();

  const displayDays = days > 0 ? t('search.filters.daysDifference', days) : '';
  const displayHours =
    hours > 0 ? t('search.filters.hoursDifference', hours) : '';
  const displayMinutes =
    minutes > 0 ? t('search.filters.minutesDifference', minutes) : '';

  return `${displayDays} ${displayHours} ${displayMinutes}`;
};

// Функции форматирования для ивентов аналитики - локализация не нужна
const formatSecondsToReadable = (seconds: number): string => {
  if (!seconds) return '0м';
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return `${hours}ч ${minutes}м`;
  }
  if (hours > 0) {
    return `${hours}ч`;
  }
  return `${minutes}м`;
};

const formatMinutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatTimeRange = (timeArray: number[]): string => {
  return `${formatMinutesToTime(timeArray[0])}-${formatMinutesToTime(
    timeArray[1],
  )}`;
};

const formatDurationRange = (durationArray: number[]): string => {
  return `${formatSecondsToReadable(
    durationArray[0],
  )}-${formatSecondsToReadable(durationArray[1])}`;
};

// Extended FilterState with additional fields used in the app
export type ExtendedFilterState = FilterState & {
  isWithoutNightTransfer?: boolean | null;
  isImplicitAllAirlines?: boolean;
};

type FilterApplicationCheckInput = {
  state: ExtendedFilterState;
  limits?: searchOffersTypes.FilterLimits;
};

/**
 * Extracts only the applied (user-modified) filters from the full filter state
 * @param input - Filter state and limits
 * @returns Object containing only the applied filters in analytics format
 */
export const getAppliedFiltersParams = (
  input: FilterApplicationCheckInput,
): Record<string, unknown> => {
  const { state } = input;
  const params: Record<string, unknown> = {};
  const appliedFilterNames: string[] = [];

  if (searchOffersLib.isPriceFilterApplied(input)) {
    params.flight_cost = state.prices;
    appliedFilterNames.push('flight_cost');
  }

  if (!state.isAllAirlines) {
    const selectedAirlines = Object.entries(state.airlines)
      .filter(([, selected]) => selected)
      .map(([iata]) => iata);
    if (selectedAirlines.length > 0) {
      params.airline_choosed = selectedAirlines.join(',');
      appliedFilterNames.push('airlines');
    }
  }

  if (searchOffersLib.isFlightTimeFiltersApplied(input)) {
    const isRoundTrip = state.flights.length > 1;
    const depTime = formatTimeRange(state.flights[0].dep.time);
    if (isRoundTrip) {
      params.city_from_time_rt = depTime;
    } else {
      params.city_from_time_ow = depTime;
    }
    appliedFilterNames.push('time');
  }

  if (searchOffersLib.isFlightDurationFilterApplied(input)) {
    params.travel_time = formatDurationRange(state.flights[0].duration);
    appliedFilterNames.push('travel_time');
  }

  if (searchOffersLib.isTransferAmountFilterApplied(input)) {
    params.transfers = state.flights[0].transfers.join(',');
    appliedFilterNames.push('transfers');
  }

  if (searchOffersLib.isWithoutNightTransferApplied(input)) {
    params.no_night_transfer = String(state.isWithoutNightTransfer);
    if (!appliedFilterNames.includes('transfers')) {
      appliedFilterNames.push('transfers');
    }
  }

  if (searchOffersLib.isTransferDurationApplied(input)) {
    params.transfers_time = formatDurationRange(
      state.flights[0].transfersDuration,
    );
    if (!appliedFilterNames.includes('transfers')) {
      appliedFilterNames.push('transfers');
    }
  }

  params.filters_applied = appliedFilterNames.join(',');

  return params;
};

export const getAirlinesSeparateAppliedParams = (
  state: ExtendedFilterState,
): { airline_choosed: string } => {
  const selectedAirlines = Object.entries(state.airlines)
    .filter(([, selected]) => selected)
    .map(([iata]) => iata);
  return { airline_choosed: selectedAirlines.join(',') };
};

export const getTimeSeparateAppliedParams = (
  state: ExtendedFilterState,
): { travel_time: string } => {
  return { travel_time: formatTimeRange(state.flights[0].dep.time) };
};

export const getTransfersSeparateAppliedParams = (
  state: ExtendedFilterState,
): { transfers: string; no_night_transfer: string } => {
  return {
    transfers: state.flights[0].transfers.join(','),
    no_night_transfer: String(!!state.isWithoutNightTransfer),
  };
};
